/**
 * QR Code Generator Service
 * Handles all API calls related to QR code generation and retrieval
 * Uses fetchWithAuth for JWT + refresh token on 401
 */

import { fetchWithAuth } from '../../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Service class for QR Code Generator API calls
 */
class QRCodeGeneratorService {
  /**
   * Generate QR codes in bulk (QR codes are generated on the backend)
   * @param {number} count - Number of QR codes to generate (1-100)
   * @param {string} prefix - Prefix for QR codes (default: "QR")
   * @returns {Promise<Object>} Response with generated QR codes and their images
   * Expected response format: { message, totalGenerated, pdfUrl, qrTag, qrCodes: [{ qrUniqueId, tagId, qrData, imageUrl }] }
   */
  static async generateQRCodes(count, prefix = 'QR') {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/qr/generate`;
      
      const response = await fetchWithAuth(apiUrl, {
        method: 'POST',
        body: JSON.stringify({ count, prefix }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `Failed to generate QR codes: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error generating QR codes:', error);
      throw error;
    }
  }

  /**
   * Get generated QR PDFs (latest first)
   * @param {number} limit - Max items to fetch
   * @returns {Promise<{count:number,items:Array,nextContinuationToken?:string,truncated?:boolean}>}
   */
  static async getAllGeneratedPdfs() {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/qr/get-all-pdfs`;

      const response = await fetchWithAuth(apiUrl, { method: 'GET' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          `Failed to fetch generated PDFs: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching generated PDFs:', error);
      throw error;
    }
  }

  /**
   * Download a generated QR PDF file by fileName (saves to disk, not inline preview)
   * @param {string} fileName - PDF file name from get-all-pdfs items[].fileName
   * @returns {Promise<Blob>} PDF binary
   */
  static async downloadQrPdfFile(fileName) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/qr/download-file?fileName=${encodeURIComponent(fileName)}`;

      const response = await fetchWithAuth(apiUrl, { method: 'GET' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          `Failed to download PDF: ${response.status} ${response.statusText}`
        );
      }

      return await response.blob();
    } catch (error) {
      console.error('Error downloading QR PDF file:', error);
      throw error;
    }
  }

  /**
   * Single QR PDF metadata (Get QR Code – QR search)
   * @param {string} qrUniqueId
   * @returns {Promise<{qrUniqueId:string,fileName:string,previewUrl:null,downloadUrl:null,expiresAt:null}>}
   */
  static async getSingleQrPdf(qrUniqueId) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/qr/single-qr-pdf`;

      const response = await fetchWithAuth(apiUrl, {
        method: 'POST',
        body: JSON.stringify({ qrUniqueId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          `Failed to fetch QR PDF: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching single QR PDF metadata:', error);
      throw error;
    }
  }

  /**
   * Download single QR PDF by fileName from metadata (Get QR Code – QR search)
   * @param {string} fileName - e.g. QR000123.pdf from single-qr-pdf response
   * @returns {Promise<Blob>}
   */
  static async downloadSingleQrPdf(fileName) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/qr/download-single-qr-pdf?fileName=${encodeURIComponent(fileName)}`;

      const response = await fetchWithAuth(apiUrl, { method: 'GET' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          `Failed to download QR PDF: ${response.status} ${response.statusText}`
        );
      }

      return await response.blob();
    } catch (error) {
      console.error('Error downloading single QR PDF:', error);
      throw error;
    }
  }

  /**
   * Download one-time PDF bytes by token URL.
   * Uses fetchWithAuth so we keep auth/refresh behavior.
   * @param {string} url - absolute one-time download URL
   * @returns {Promise<Blob>}
   */
  static async downloadOneTimePdf(url) {
    // Many one-time links are pre-signed (e.g., S3) and will fail CORS if we attach Authorization headers.
    // Use authenticated fetch only for same-origin / API_BASE_URL URLs; otherwise use plain fetch.
    const isSameOriginUrl = (() => {
      try {
        const abs = new URL(url, window.location.origin);
        const apiBase = API_BASE_URL ? new URL(API_BASE_URL, window.location.origin) : null;
        if (apiBase && abs.origin === apiBase.origin) return true;
        return abs.origin === window.location.origin;
      } catch {
        // If URL parsing fails (very unlikely), fall back to authenticated fetch.
        return true;
      }
    })();

    const response = isSameOriginUrl
      ? await fetchWithAuth(url, { method: 'GET' })
      : await fetch(url, { method: 'GET' });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
        `Failed to download PDF: ${response.status} ${response.statusText}`
      );
    }
    return await response.blob();
  }

  /**
   * Search QR PDFs by companyId + siteId (returns one-time links list)
   * @param {number|string} companyId
   * @param {number|string} siteId
   * @param {number} limit
   * @returns {Promise<{count:number,items:Array}>}
   */
  static async searchByCompanySite(companyId, siteId, limit = 25) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/qr/search-by-company-site?companyId=${encodeURIComponent(companyId)}&siteId=${encodeURIComponent(siteId)}&limit=${encodeURIComponent(limit)}`;
      const response = await fetchWithAuth(apiUrl, { method: 'GET' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          `Failed to search QR PDFs: ${response.status} ${response.statusText}`
        );
      }
      return await response.json();
    } catch (error) {
      console.error('Error searching by company/site:', error);
      throw error;
    }
  }

  /**
   * Search QR PDF by request id (returns a single one-time link)
   * @param {string} requestId
   * @returns {Promise<{qrUniqueId:string,fileName:string,previewUrl:string,downloadUrl:string,expiresAt:number}>}
   */
  static async searchByRequestId(requestId) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/qr/search-by-request-id?requestId=${encodeURIComponent(requestId)}`;
      const response = await fetchWithAuth(apiUrl, { method: 'GET' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
          `Failed to search by request id: ${response.status} ${response.statusText}`
        );
      }
      return await response.json();
    } catch (error) {
      console.error('Error searching by request id:', error);
      throw error;
    }
  }

  /**
   * Get all companies
   * @returns {Promise<Array>} List of companies
   */
  static async getCompanies() {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/get-all-companies-admin`;
      
      const response = await fetchWithAuth(apiUrl, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Failed to fetch companies: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Handle different API response formats
      if (data.companies && Array.isArray(data.companies)) {
        return data.companies;
      }
      if (Array.isArray(data)) {
        return data;
      }

      return [];
    } catch (error) {
      console.error('Error fetching companies:', error);
      throw error;
    }
  }

  /**
   * Get state list
   * @returns {Promise<Array>} List of states
   */
  static async getStateList() {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/state-list`;
      const response = await fetchWithAuth(apiUrl, { method: 'GET' });
      if (!response.ok) {
        throw new Error(`Failed to fetch states: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching states:', error);
      throw error;
    }
  }

  /**
   * Get company sites by company ID
   * @param {number} companyId - Company ID
   * @returns {Promise<Array>} List of company sites
   */
  static async getCompanySites(companyId) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/company/get-all-sites?companyId=${encodeURIComponent(companyId)}`;
      
      const response = await fetchWithAuth(apiUrl, { method: 'GET' });

      if (!response.ok) {
        throw new Error(`Failed to fetch company sites: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Handle different API response formats
      if (data.sites && Array.isArray(data.sites)) return data.sites;
      if (Array.isArray(data)) {
        return data;
      }

      return [];
    } catch (error) {
      console.error('Error fetching company sites:', error);
      throw error;
    }
  }

  /**
   * Get QR code by QrUniqueId (QR code image is generated on the backend)
   * @param {string} qrUniqueId - QR unique ID
   * @param {number} companyId - Company ID
   * @param {number} siteId - Site ID
   * @returns {Promise<Object>} QR code data with image
   * Expected response format: { qrCode: { qrUniqueId, qrCodeImage/qrCodeUrl/imageBase64, ... } }
   */
  static async getQRCodeByUniqueId(qrUniqueId, companyId, siteId) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/qr-codes/by-unique-id`;
      
      const response = await fetchWithAuth(apiUrl, {
        method: 'POST',
        body: JSON.stringify({
          qrUniqueId,
          companyId,
          siteId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `Failed to fetch QR code: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching QR code by unique ID:', error);
      throw error;
    }
  }

  /**
   * Get QR code by machinery name (QR code image is generated on the backend)
   * @param {string} machineryName - Machinery name
   * @param {number} companyId - Company ID
   * @param {number} siteId - Site ID
   * @returns {Promise<Object>} QR code data with image
   * Expected response format: { qrCode: { machineryName, qrCodeImage/qrCodeUrl/imageBase64, ... } }
   */
  static async getQRCodeByMachineryName(machineryName, companyId, siteId) {
    try {
      const apiUrl = `${API_BASE_URL}/api/v1/qr-codes/by-machinery-name`;
      
      const response = await fetchWithAuth(apiUrl, {
        method: 'POST',
        body: JSON.stringify({
          machineryName,
          companyId,
          siteId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `Failed to fetch QR code: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching QR code by machinery name:', error);
      throw error;
    }
  }
}

export default QRCodeGeneratorService;

